import React, { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Instagram, Facebook, Mail, Phone, MapPin } from 'lucide-react';
import logoImg from '../assets/logo.png';
import { LEGAL_DOCS } from '../data/legalContent';

const LegalPage = () => {
  const { slug } = useParams();
  const doc = LEGAL_DOCS[slug];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!doc) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="bg-[#142d16] border-b border-[#FFB800]/20" data-testid="legal-header">
        <div className="container mx-auto px-4 md:px-8 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center" data-testid="legal-logo-link">
            <img src={logoImg} alt="Pizzapur" className="h-12 md:h-14" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/85 hover:text-[#FFB800] text-sm font-semibold transition-colors"
            data-testid="legal-back-home"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Home
          </Link>
        </div>
      </header>

      {/* Hero band */}
      <section
        className="bg-[#142d16] py-16 md:py-20 px-4 relative overflow-hidden"
        data-testid={`legal-page-${slug}`}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 50% 60% at 50% 50%, rgba(255,184,0,0.10) 0%, transparent 70%)',
          }}
        />
        <div className="relative container mx-auto max-w-4xl text-center">
          <span className="inline-block px-4 py-1 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#FFB800] text-xs font-bold tracking-widest uppercase mb-4">
            Legal
          </span>
          <h1 className="font-bebas text-white text-5xl md:text-7xl leading-[0.95] tracking-wide">
            {doc.title}
          </h1>
          <p className="text-white/60 text-sm mt-4">Last Updated: {doc.lastUpdated}</p>
        </div>
      </section>

      {/* Body */}
      <main className="flex-1 py-12 md:py-16 px-4">
        <div className="container mx-auto max-w-3xl">
          <article className="prose prose-lg max-w-none">
            {doc.sections.map((s, i) => (
              <section key={i} className="mb-10" data-testid={`legal-section-${i}`}>
                <h2 className="font-bebas text-[#142d16] text-2xl md:text-3xl tracking-wide mb-3">
                  <span className="text-[#FFB800] mr-2">{String(i + 1).padStart(2, '0')}.</span>
                  {s.heading}
                </h2>
                {s.body && (
                  <p className="text-gray-700 text-base leading-relaxed">{s.body}</p>
                )}
                {s.bullets && (
                  <ul className="mt-3 space-y-2 pl-1">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex gap-3 text-gray-700 text-base leading-relaxed">
                        <span className="text-[#FFB800] font-bold flex-shrink-0 mt-1">•</span>
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.footer && (
                  <p className="text-gray-600 text-sm mt-3 italic">{s.footer}</p>
                )}
              </section>
            ))}
          </article>

          {/* Related links */}
          <div className="mt-12 pt-8 border-t border-gray-200 flex flex-wrap gap-3" data-testid="legal-related">
            {Object.entries(LEGAL_DOCS)
              .filter(([s]) => s !== slug)
              .map(([s, d]) => (
                <Link
                  key={s}
                  to={`/legal/${s}`}
                  className="inline-flex items-center gap-2 bg-gray-100 hover:bg-[#FFB800]/15 text-[#142d16] hover:text-[#142d16] px-4 py-2 rounded-full text-sm font-semibold transition-colors"
                  data-testid={`legal-related-${s}`}
                >
                  {d.title} <span aria-hidden>→</span>
                </Link>
              ))}
          </div>
        </div>
      </main>

      {/* Simple shared footer */}
      <footer className="bg-[#2D6B2F] text-white border-t-4 border-[#FFB800] mt-12" data-testid="legal-footer">
        <div className="container mx-auto px-4 py-12 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="space-y-4">
              <img src={logoImg} alt="Pizzapur" style={{ width: '150px', height: 'auto' }} />
              <p className="text-sm text-white/70 max-w-xs">
                India's Most Affordable &amp; Profitable Pizza Franchise
              </p>
              <div className="flex items-center gap-3 pt-1">
                <a
                  href="https://www.instagram.com/pizzapur/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="w-9 h-9 rounded-full border border-white/60 flex items-center justify-center hover:bg-white hover:text-[#2D6B2F] transition-colors"
                >
                  <Instagram className="h-4 w-4" />
                </a>
                <a
                  href="https://www.facebook.com/pizzapurindia"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="w-9 h-9 rounded-full border border-white/60 flex items-center justify-center hover:bg-white hover:text-[#2D6B2F] transition-colors"
                >
                  <Facebook className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              <h4 className="text-white font-semibold uppercase tracking-wider text-xs mb-3">
                Contact
              </h4>
              <a
                href="mailto:contact@pizzapur.com"
                className="flex items-center gap-2 text-white/80 hover:text-white"
              >
                <Mail className="h-4 w-4 text-white/60" /> contact@pizzapur.com
              </a>
              <a
                href="https://api.whatsapp.com/send/?phone=917877052363"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-white/80 hover:text-white"
              >
                <Phone className="h-4 w-4 text-white/60" /> WhatsApp: 7877052363
              </a>
              <div className="flex items-start gap-2 text-white/80">
                <MapPin className="h-4 w-4 text-white/60 mt-0.5" />
                <span>Kudi Sector 2, Jodhpur, Rajasthan, India</span>
              </div>
            </div>
            <div className="space-y-3 text-sm">
              <h4 className="text-white font-semibold uppercase tracking-wider text-xs mb-3">
                Legal
              </h4>
              <Link to="/legal/privacy" className="block text-white/80 hover:text-white">
                Privacy Policy
              </Link>
              <Link to="/legal/terms" className="block text-white/80 hover:text-white">
                Terms &amp; Conditions
              </Link>
              <Link to="/legal/refund" className="block text-white/80 hover:text-white">
                Refund Policy
              </Link>
            </div>
          </div>
          <div className="border-t border-white/15 mt-10 pt-6">
            <p className="text-xs text-white/60 text-center">
              © 2025 Navyaji Food Industry Private Limited. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
